import { Box, CircularProgress, Typography, Link, Stack } from '@mui/material';
import { useGetTrainingVideosQuery } from '../../../api/models/help';
import { PageContainer } from 'app';

export const TrainingVideosPage: React.FC = () => {
  const { data: videos, isLoading, error } = useGetTrainingVideosQuery();

  const scrollToVideo = (videoId: string) => {
    const element = document.getElementById(`video-${videoId}`);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          padding: 2,
        }}
      >
        <Typography color="error">
          Failed to load training videos. Please try again later.
        </Typography>
      </Box>
    );
  }

  if (!videos || videos.length === 0) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          padding: 2,
        }}
      >
        <Typography>No training videos available.</Typography>
      </Box>
    );
  }

  return (
    <PageContainer>
      <Typography variant="h6" sx={{ marginBottom: 3 }}>
        Training Videos
      </Typography>
      <Box sx={{ display: 'flex', gap: 4 }}>
        <Box
          sx={{
            width: '250px',
            flexShrink: 0,
            position: 'sticky',
            top: 100,
            alignSelf: 'flex-start',
            backgroundColor: 'background.paper',
            borderRadius: 1,
            border: '1px solid',
            borderColor: 'divider',
            padding: 2,
            maxHeight: 'calc(100vh - 120px)',
            overflowY: 'auto',
          }}
        >
          <Typography
            variant="subtitle2"
            sx={{ marginBottom: 2, fontWeight: 600, color: 'text.secondary' }}
          >
            Table of Contents
          </Typography>
          <Stack spacing={0.5}>
            {videos.map(video => (
              <Link
                key={video.id}
                component="button"
                variant="body2"
                onClick={() => scrollToVideo(video.id)}
                sx={{
                  textAlign: 'left',
                  cursor: 'pointer',
                  textDecoration: 'none',
                  padding: 1,
                  borderRadius: 0.5,
                  color: 'text.secondary',
                  transition: 'color 0.2s',
                  '&:hover': {
                    color: 'primary.main',
                    textDecoration: 'none',
                  },
                }}
              >
                {video.title}
              </Link>
            ))}
          </Stack>
        </Box>

        <Box sx={{ flex: 1 }}>
          <Stack spacing={4}>
            {videos.map(video => (
              <Box
                key={video.id}
                id={`video-${video.id}`}
                sx={{ scrollMarginTop: '100px' }}
              >
                <Typography variant="h6" sx={{ marginBottom: 1 }}>
                  {video.title}
                </Typography>
                {video.description && (
                  <Typography
                    variant="body2"
                    sx={{ marginBottom: 2, color: 'text.secondary' }}
                  >
                    {video.description}
                  </Typography>
                )}
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    width: '100%',
                  }}
                >
                  <iframe
                    src={video.embedUrl}
                    width="100%"
                    height="360"
                    frameBorder="0"
                    scrolling="no"
                    allowFullScreen
                    style={{ maxWidth: '640px' }}
                  />
                </Box>
              </Box>
            ))}
          </Stack>
        </Box>
      </Box>
    </PageContainer>
  );
};
